// Print + PDF export helper for Cocoa EHR.
//
// The rendered HTML must match the application: same Inter body, same Manrope
// headings, same warm cocoa palette. Every printed document that carries a
// signature MUST include the universal audit block emitted by
// `signatureAuditHtml`. Do not roll your own inline signature markup, the audit
// block guarantees signature_id, doc_hash, IP, timestamp, ESIGN citation, and
// tamper-evidence appear consistently across the entire app.

export function openPrintWindow(title, bodyHtml, opts = {}) {
  let org = {};
  try { org = JSON.parse(sessionStorage.getItem("org_branding")) || {}; } catch { org = {}; }
  const accent = org.accent_color || "#4E342E";
  const subline = [org.address, [org.city, org.state].filter(Boolean).join(", "), org.zip].filter(Boolean).join(" · ");
  const letterhead = `
    <div class="letterhead">
      ${org.logo ? `<img src="${org.logo}" alt="logo" class="lh-logo" />` : ""}
      <div>
        <div class="lh-name">${esc(org.name || "Cocoa EHR")}</div>
        <div class="lh-sub">${esc(subline)}${org.phone ? `${subline ? " · " : ""}${esc(org.phone)}` : ""}</div>
      </div>
    </div>
    <div class="lh-bar"></div>`;
  const footer = org.footer_text && opts.letterhead !== false ? `<div class="lh-footer">${esc(org.footer_text)}</div>` : "";
  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Manrope:wght@500;600;700;800&family=Inter:wght@400;500;600;700&display=swap" />
    <style>
    * { box-sizing: border-box; }
    body { font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; color: #3E2723; margin: 48px 56px; line-height: 1.55; }
    h1, h2, h3, h4 { font-family: 'Manrope', system-ui, sans-serif; font-weight: 700; }
    h1 { font-size: 22px; margin: 0 0 2px; color: #3E2723; letter-spacing: -0.01em; }
    h2 { font-size: 13px; margin: 22px 0 8px; color: #4E342E; border-bottom: 2px solid #D9C7B2; padding-bottom: 5px; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 700; }
    .letterhead { display: flex; align-items: center; gap: 16px; }
    .lh-logo { height: 52px; max-width: 180px; object-fit: contain; }
    .lh-name { font-family: 'Manrope', system-ui, sans-serif; font-size: 20px; font-weight: 800; color: ${accent}; letter-spacing: -0.01em; }
    .lh-sub { font-size: 11px; color: #8D6E63; }
    .lh-bar { height: 4px; background: ${accent}; border-radius: 2px; margin: 10px 0 22px; }
    .lh-footer { font-size: 10.5px; color: #A1887F; margin-top: 28px; border-top: 1px solid #D9C7B2; padding-top: 8px; text-align: center; }
    .clinic { font-size: 12px; color: #8D6E63; margin-bottom: 24px; border-bottom: 3px solid ${accent}; padding-bottom: 12px; }
    table { width: 100%; border-collapse: collapse; font-size: 12.5px; margin: 6px 0 12px; }
    th { text-align: left; color: #8D6E63; font-size: 10.5px; text-transform: uppercase; padding: 4px 8px 4px 0; border-bottom: 1px solid #D9C7B2; letter-spacing: 0.04em; font-weight: 600; }
    td { padding: 5px 8px 5px 0; border-bottom: 1px solid #EFEBE0; vertical-align: top; }
    .meta { font-size: 11px; color: #A1887F; margin-top: 32px; border-top: 1px solid #D9C7B2; padding-top: 10px; }
    .row { display: flex; gap: 24px; } .row > div { flex: 1; }
    .label { font-size: 10.5px; text-transform: uppercase; color: #8D6E63; letter-spacing: 0.04em; font-weight: 600; }
    .value { font-size: 13px; margin-bottom: 8px; }
    .letter-body { white-space: pre-wrap; font-size: 14px; margin-top: 16px; }
    .banner { border: 2px solid ${accent}; padding: 10px 14px; font-weight: 700; text-align: center; margin: 14px 0; font-size: 13px; letter-spacing: 0.08em; }
    .mono, pre, code { font-family: 'JetBrains Mono', 'Menlo', 'Courier New', monospace !important; }

    /* Universal signature audit block. Used for every printed signature so all
       documents show the same tamper-evident metadata. */
    .sig-audit { margin: 22px 0 4px; page-break-inside: avoid; }
    .sig-audit .sig-img-wrap { border-bottom: 1.5px solid #6D4C41; display: inline-block; padding: 2px 30px 2px 4px; margin-bottom: 6px; }
    .sig-audit .sig-img-wrap img { display: block; height: 60px; max-width: 320px; object-fit: contain; }
    .sig-audit .sig-typed { font-family: 'Dancing Script', 'Segoe Script', cursive; font-size: 26px; color: #3E2723; padding: 6px 24px 4px 4px; border-bottom: 1.5px solid #6D4C41; display: inline-block; margin-bottom: 6px; }
    .sig-audit .sig-verified { display: inline-flex; align-items: center; gap: 6px; font-size: 10.5px; font-weight: 700; color: #2E7D32; background: #E8F5E9; padding: 2px 8px; border-radius: 999px; letter-spacing: 0.02em; }
    .sig-audit .sig-verified::before { content: "✓"; }
    .sig-audit .sig-block { margin-top: 8px; border: 1px solid #D9C7B2; border-left: 4px solid #4E342E; border-radius: 4px; padding: 10px 14px; background: #FBF8F1; font-size: 10.5px; color: #3E2723; }
    .sig-audit .sig-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 4px 22px; margin-top: 6px; }
    .sig-audit .sig-grid > div { font-size: 10.5px; color: #4E342E; }
    .sig-audit .sig-grid b { color: #3E2723; font-weight: 600; }
    .sig-audit .sig-full { grid-column: 1 / -1; }
    .sig-audit .sig-hash { font-family: 'JetBrains Mono', 'Menlo', 'Courier New', monospace; color: #5D4037; word-break: break-all; }
    .sig-audit .sig-esign { grid-column: 1 / -1; font-size: 9.5px; color: #8D6E63; font-style: italic; margin-top: 4px; padding-top: 6px; border-top: 1px dashed #D9C7B2; }

    .sig-audit.compact { margin: 8px 0 2px; }
    .sig-audit.compact .sig-img-wrap img { height: 34px; }
    .sig-audit.compact .sig-typed { font-size: 18px; padding: 2px 16px 2px 4px; }
    .sig-audit.compact .sig-block { padding: 5px 8px; margin-top: 4px; }
    .sig-audit.compact .sig-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 2px 10px; }
    .sig-audit.compact .sig-grid > div, .sig-audit.compact .sig-esign { font-size: 8px; }
    @media print { body { margin: 24px; } .sig-audit { page-break-inside: avoid; } }
    ${opts.css || ""}
  </style></head><body>${opts.letterhead === false ? "" : letterhead}${bodyHtml}${footer}</body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 500);
}

export const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Format an ISO or Date to `Mon D, YYYY h:mm A` without pulling in dayjs from
// callers. Prints must be self-sufficient because `openPrintWindow` runs inside
// a detached window.
function _fmt(when) {
  if (!when) return "-";
  try {
    const d = when instanceof Date ? when : new Date(when);
    if (isNaN(d.getTime())) return String(when);
    const opts = { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
    return d.toLocaleString("en-US", opts);
  } catch { return String(when); }
}

/*
 * Universal Signature Audit Block for prints.
 *
 * Renders the signer's visible signature (drawn image or typed name) plus the
 * full tamper-evidence stamp (signature_id, signer name and role, timestamp,
 * IP address, document hash, ESIGN Act and HIPAA citations).
 *
 * Call this whenever you print a signed document. Supports two input shapes:
 *   1. A signature object: {signature_id, name, image, signed_at, doc_hash,
 *      signer_name, signer_type, ip}
 *   2. A legacy document with root-level fields: {signed_by, signed_at,
 *      signature_image, signature_name, signature_id, signature: {...}}
 *
 * Returns "" if the document has never been signed so callers can drop it in
 * unconditionally.
 */
export function signatureAuditHtml(input, opts) {
  if (!input) return "";
  opts = opts || {};
  const sig = input.signature || {};
  const image = input.image || sig.image || input.signature_image || "";
  const typedName = input.name || sig.name || input.signature_name || input.signed_by || "";
  const sigId = input.signature_id || sig.signature_id || "";
  const signedAt = input.signed_at || sig.signed_at || "";
  const signerName = input.signer_name || sig.signer_name || input.signed_by || typedName;
  const signerType = input.signer_type || sig.signer_type || opts.signer_type || "Staff";
  const ip = input.ip || sig.ip || "";
  const hash = input.doc_hash || sig.doc_hash || "";
  const cosignedBy = input.cosigned_by || opts.cosigned_by || "";
  const cosignedAt = input.cosigned_at || opts.cosigned_at || "";
  const label = opts.label || "Electronic Signature";
  const compact = !!opts.compact;
  if (!image && !typedName && !sigId) return "";
  const visible = image
    ? `<div class="sig-img-wrap"><img src="${image}" alt="Signature of ${esc(signerName || typedName)}" /></div>`
    : `<div class="sig-typed">${esc(typedName)}</div>`;
  const verified = sigId ? `<div style="margin-top:4px"><span class="sig-verified">Verified E-Signature</span></div>` : "";
  const rows = [];
  rows.push(`<div><b>Signature ID</b><br/><span class="mono">${esc(sigId || "not recorded")}</span></div>`);
  rows.push(`<div><b>Signed by</b><br/>${esc(signerName || "-")}${signerType ? ` <span style="color:#8D6E63">(${esc(signerType)})</span>` : ""}</div>`);
  rows.push(`<div><b>Timestamp</b><br/>${esc(_fmt(signedAt))}</div>`);
  rows.push(`<div><b>IP address</b><br/>${esc(ip || "not recorded")}</div>`);
  if (hash) rows.push(`<div class="sig-full"><b>Document hash (SHA-256)</b><br/><span class="sig-hash">${esc(hash)}</span></div>`);
  if (cosignedBy) rows.push(`<div class="sig-full"><b>Co-signed by</b> ${esc(cosignedBy)}${cosignedAt ? ` on ${esc(_fmt(cosignedAt))}` : ""}</div>`);
  rows.push(`<div class="sig-esign">Signed under the ESIGN Act (15 U.S.C. Chapter 96) and HIPAA (45 CFR 164.312). Any modification to the signed content invalidates the document hash.</div>`);
  return `
    <div class="sig-audit${compact ? " compact" : ""}">
      <div class="label" style="margin-bottom:${compact ? 2 : 6}px">${esc(label)}</div>
      ${visible}
      ${verified}
      <div class="sig-block">
        <div style="font-weight:700; font-size:11px; color:#3E2723; letter-spacing:0.03em; text-transform:uppercase">Signature Audit Trail</div>
        <div class="sig-grid">${rows.join("")}</div>
      </div>
    </div>`;
}

// Backward-compatible short meta line used in some print footers. Callers that
// already render the full audit block via `signatureAuditHtml` should NOT also
// print this line, it would be duplicative.
export function signatureMetaLine(row) {
  if (!row) return "UNSIGNED DRAFT";
  if (!row.signed) return "UNSIGNED DRAFT";
  const who = row.signed_by || row.signature_name || (row.signature && (row.signature.signer_name || row.signature.name)) || "signer";
  const at = _fmt(row.signed_at || (row.signature && row.signature.signed_at));
  const id = row.signature_id || (row.signature && row.signature.signature_id) || "";
  return `Electronically signed by ${esc(who)} on ${esc(at)}${id ? ` · ID <span class="mono">${esc(id)}</span>` : ""}`;
}
